import { resolveRoles } from "./module-access";

export const ADMIN_PERMISSION_GROUPS = {
  dashboard: [
    "admin.dashboard.view",
    "admin.users.view",
    "admin.rbac.roles.view",
    "admin.rbac.permissions.view",
    "admin.broadcast.view",
  ],
  users: [
    "admin.users.view",
    "admin.users.create",
    "admin.users.update",
    "admin.users.delete",
  ],
  rbac: [
    "admin.rbac.roles.view",
    "admin.rbac.roles.create",
    "admin.rbac.roles.update",
    "admin.rbac.roles.delete",
    "admin.rbac.roles.assign",
    "admin.rbac.permissions.view",
    "admin.rbac.permissions.sync",
    "admin.rbac.permissions.update",
  ],
  broadcast: [
    "admin.broadcast.view",
    "admin.broadcast.send",
  ],
} as const;

export const ADMIN_ANY_PERMISSIONS = Array.from(
  new Set(Object.values(ADMIN_PERMISSION_GROUPS).flat()),
);

export function parsePermissionList(raw: string | null | undefined): string[] {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : [];
  } catch {
    return [];
  }
}

export function hasAnyPermission(
  permissions: string[] | null | undefined,
  required: readonly string[],
  roles?: string[] | null,
): boolean {
  // Use passed roles or try to resolve them from storage (client-side only)
  const effectiveRoles = roles ?? resolveRoles();
  if (effectiveRoles.includes("super_admin")) return true;

  if (!Array.isArray(permissions) || permissions.length === 0) {
    return false;
  }

  const permissionSet = new Set(permissions);
  if (permissionSet.has("*")) return true;

  return required.some((permission) => permissionSet.has(permission));
}

export function canAccessAdmin(
  permissions: string[] | null | undefined,
  roles?: string[] | null,
): boolean {
  return hasAnyPermission(permissions, ADMIN_ANY_PERMISSIONS, roles);
}

export function canAccessAdminPath(
  pathname: string,
  permissions: string[] | null | undefined,
  roles?: string[] | null,
): boolean {
  const effectiveRoles = roles ?? resolveRoles();

  if (pathname.startsWith("/admin/so-fast-track")) {
    return effectiveRoles.includes("super_admin");
  }

  if (pathname === "/admin" || pathname === "/admin/") {
    return canAccessAdmin(permissions, effectiveRoles);
  }

  if (pathname.startsWith("/admin/rbac")) {
    return hasAnyPermission(permissions, ADMIN_PERMISSION_GROUPS.rbac, effectiveRoles);
  }

  if (pathname.startsWith("/admin/users")) {
    return hasAnyPermission(permissions, ADMIN_PERMISSION_GROUPS.users, effectiveRoles);
  }

  if (pathname.startsWith("/admin/broadcast")) {
    return hasAnyPermission(permissions, ADMIN_PERMISSION_GROUPS.broadcast, effectiveRoles);
  }

  return canAccessAdmin(permissions, effectiveRoles);
}

export function readStoredPermissions(): string[] {
  if (typeof window === "undefined") {
    return [];
  }

  const direct = parsePermissionList(localStorage.getItem("rbac_permissions"));
  if (direct.length > 0) {
    return direct;
  }

  try {
    const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
    return Array.isArray(userData?.rbac_permissions)
      ? userData.rbac_permissions.filter(
          (item: unknown): item is string => typeof item === "string",
        )
      : [];
  } catch {
    return [];
  }
}
